import { Button } from "@/components/ui/baseUi/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useCalendar } from "@/modules/calendar/hooks/useCalendar"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useFlipActionsPanel } from "@/modules/programs/hooks/useFlipActionsPanel"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { ChevronDownIcon, ChevronUpIcon, PencilIcon } from "lucide-react"

export function ProgramCalendarSessionBlockContainer({
  children,
  canSeeActions,
  orderIndex,
  session,
  firstBlockIndex,
  lastBlockIndex,
  onBlockEditClick,
  onMoveBlock,
  className,
  ...props
}: {
  children: React.ReactNode
  canSeeActions: boolean
  orderIndex: number
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  firstBlockIndex: number
  lastBlockIndex: number
  onBlockEditClick: (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity, orderIndex: number) => void
  onMoveBlock: (direction: "UP" | "DOWN", orderIndex: number) => void
} & React.ComponentProps<"div">) {
  const { isDragging } = useCalendar()
  const { containerRef, shouldFlipToLeft, checkAvailableSpace } = useFlipActionsPanel()

  const onSortBlockUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (orderIndex === firstBlockIndex) return

    onMoveBlock("UP", orderIndex)
  }

  const onSortBlockDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (orderIndex === lastBlockIndex) return

    onMoveBlock("DOWN", orderIndex)
  }

  const onEditBlock = (e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>) => {
    if (!canSeeActions) return
    e.stopPropagation()

    if (session) {
      onBlockEditClick(session, orderIndex)
    }
  }

  return (
    <div
      ref={containerRef}
      className={cn("group relative flex w-full flex-row items-start", className)}
      onClick={(e) => void onEditBlock(e)}
      onMouseEnter={checkAvailableSpace}
      {...props}
    >
      <div className="w-full rounded-2xl px-2 py-1 group-hover:bg-primary-100">{children}</div>

      {canSeeActions && !isDragging && (
        <div
          className={cn(
            "absolute z-3 hidden animate-(--animate-flip) flex-row py-1 group-hover:flex",
            shouldFlipToLeft && "flex-row-reverse",
          )}
          style={shouldFlipToLeft ? { left: "-2.7rem" } : { right: "-2.7rem" }}
        >
          {/* Empty space to avoid the hover to be lost between the block and the actions */}
          <div className="h-full w-2" />
          <div
            className="w-2rem flex flex-col items-center justify-center gap-4 rounded-2xl bg-primary-100 p-1"
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            <ActionButton
              tooltip={word("program.session.block.edit")}
              onClick={(e) => void onEditBlock(e)}
              tooltipSide={shouldFlipToLeft ? "left" : "right"}
            >
              <PencilIcon size={16} color="currentColor" strokeWidth={2} />
            </ActionButton>

            <div className="flex flex-col gap-2">
              <ActionButton
                tooltip={word("program.session.block.move.up")}
                onClick={onSortBlockUp}
                disabled={orderIndex === firstBlockIndex}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <ChevronUpIcon size={16} />
              </ActionButton>

              <ActionButton
                tooltip={word("program.session.block.move.down")}
                onClick={onSortBlockDown}
                disabled={orderIndex === lastBlockIndex}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <ChevronDownIcon size={16} />
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ActionButton({
  children,
  tooltip,
  onClick,
  buttonClassName,
  tooltipSide = "right",
  ...buttonProps
}: React.ComponentProps<typeof Button> & {
  children: React.ReactNode
  tooltip: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  buttonClassName?: string
  tooltipSide?: "left" | "right"
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="iconXs"
            variant="secondary"
            onClick={onClick}
            className={cn("transition-colors duration-200 hover:bg-neutral-100", buttonClassName)}
            {...buttonProps}
          >
            {children}
          </Button>
        }
      />
      <TooltipContent side={tooltipSide} align="center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}
